import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useMemo } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../lib/api-client';

// Fix for default marker icons in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface RwandaMapProps {
  height?: number;
  params?: {
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
  };
}

// Pre-compute the 4 possible icons — never recreated after module load
const ICONS: Record<string, L.DivIcon> = {
  excellent: makeIcon('#10B981'),
  good:      makeIcon('#F59E0B'),
  fair:      makeIcon('#FB923C'),
  poor:      makeIcon('#EF4444'),
};

function makeIcon(color: string): L.DivIcon {
  return L.divIcon({
    html: `<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><circle cx="7" cy="7" r="5.5" fill="${color}" stroke="white" stroke-width="1.5"/></svg>`,
    className: 'custom-marker-dot',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -7],
  });
}

function getIcon(score: number): L.DivIcon {
  if (score >= 90) return ICONS.excellent;
  if (score >= 75) return ICONS.good;
  if (score >= 60) return ICONS.fair;
  return ICONS.poor;
}

const RWANDA_BOUNDS = L.latLngBounds([-2.9, 28.7], [-1.0, 31.1]);

function parseCoordinate(coordString: string): [number, number] | null {
  const parts = coordString.trim().split(',');
  if (parts.length !== 2) return null;
  const lat = Number(parts[0].trim());
  const lng = Number(parts[1].trim());
  if (isNaN(lat) || isNaN(lng)) return null;
  if (lat < -2.84 || lat > -1.05 || lng < 28.85 || lng > 30.9) return null;
  return [lat, lng];
}

// Memoized individual marker so Leaflet doesn't re-mount on parent re-renders
const FacilityMarker = React.memo(function FacilityMarker({
  coord, score, facilityName,
}: { coord: [number, number]; score: number; facilityName: string }) {
  return (
    <Marker position={coord} icon={getIcon(score)}>
      <Popup>
        <div className='p-2'>
          <h3 className='font-semibold text-gray-900 mb-1'>{facilityName}</h3>
          <div className='text-sm'>
            <span className='text-gray-600'>Score: </span>
            <span className='font-medium'>{score.toFixed(1)}%</span>
          </div>
          <div className='text-xs text-gray-500 mt-1'>
            {coord[0].toFixed(4)}, {coord[1].toFixed(4)}
          </div>
        </div>
      </Popup>
    </Marker>
  );
});

function FitBounds({ coords }: { coords: [number, number][] }) {
  const map = useMap();

  useEffect(() => {
    if (!map?.getContainer()) return;
    const id = setTimeout(() => {
      try {
        if (coords.length > 0) {
          const bounds = L.latLngBounds(coords);
          if (bounds.isValid()) {
            map.fitBounds(bounds.extend(RWANDA_BOUNDS), { padding: [50, 50], maxZoom: 11 });
            return;
          }
        }
        map.fitBounds(RWANDA_BOUNDS, { padding: [50, 50], maxZoom: 11 });
      } catch {
        try { map.fitBounds(RWANDA_BOUNDS, { padding: [50, 50], maxZoom: 11 }); } catch { /* ignore */ }
      }
    }, 100);
    return () => clearTimeout(id);
  }, [coords, map]);

  return null;
}

const RWANDA_CENTER: [number, number] = [-1.9403, 29.8739];
const RWANDA_MAX_BOUNDS: [[number, number], [number, number]] = [[-2.9, 28.7], [-1.0, 31.1]];

const RwandaMap: React.FC<RwandaMapProps> = ({ height = 400, params }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['map-data', params],
    queryFn: () => analyticsApi.getMapDataForType(params),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Parse and filter once when data changes — not on every render
  const validMarkers = useMemo(() => {
    if (!data) return [];
    const result: { coord: [number, number]; score: number; facilityName: string }[] = [];
    for (const f of data) {
      const coord = parseCoordinate(f.coordinates);
      if (coord) result.push({ coord, score: f.score, facilityName: f.facilityName });
    }
    return result;
  }, [data]);

  // Stable coords array for FitBounds — only recalculated when markers change
  const coords = useMemo(() => validMarkers.map(m => m.coord), [validMarkers]);

  if (isLoading) {
    return (
      <div className='rounded-lg border border-gray-200 flex items-center justify-center' style={{ height }}>
        <div className='h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-primary' />
      </div>
    );
  }

  if (validMarkers.length === 0) {
    return (
      <div className='rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 text-sm' style={{ height }}>
        No map data available
      </div>
    );
  }

  return (
    <div className='rounded-lg overflow-hidden border border-gray-200 -z-50' style={{ height }}>
      <MapContainer
        center={RWANDA_CENTER}
        zoom={9}
        minZoom={8}
        maxZoom={13}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        maxBounds={RWANDA_MAX_BOUNDS}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />

        {validMarkers.map((m, i) => (
          <FacilityMarker key={i} coord={m.coord} score={m.score} facilityName={m.facilityName} />
        ))}

        <FitBounds coords={coords} />
      </MapContainer>
    </div>
  );
};

export default RwandaMap;
